import React, { useState, useEffect } from 'react';
import { Calendar, RefreshCw, CheckCircle, XCircle, AlertCircle, Clock } from 'lucide-react';
import { CalendarSyncService } from '../services/calendarSyncService';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

export const CalendarSyncSettings: React.FC = () => {
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [lastSyncResult, setLastSyncResult] = useState<{
    created: number;
    updated: number;
    skipped: number;
    errors: string[];
  } | null>(null);
  const [testing, setTesting] = useState(false);
  const [totalSynced, setTotalSynced] = useState<number>(0);

  useEffect(() => {
    loadLastSyncData();
  }, []);

  const loadLastSyncData = async () => {
    // Load from localStorage for immediate display
    const stored = localStorage.getItem('lastCalendarSync');
    if (stored) {
      setLastSyncTime(stored);
    }
    
    const storedResult = localStorage.getItem('lastCalendarSyncResult');
    if (storedResult) {
      try {
        setLastSyncResult(JSON.parse(storedResult));
      } catch (e) {
        console.error('Failed to parse last sync result:', e);
      }
    }

    // Fetch from database for accurate data
    try {
      const { data, error } = await supabase
        .from('calendar_sync_log')
        .select('last_synced_at')
        .order('last_synced_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data && !error && 'last_synced_at' in data) {
        setLastSyncTime(data.last_synced_at);
        localStorage.setItem('lastCalendarSync', data.last_synced_at);
      }

      // Get total count of synced releases
      const { count } = await supabase
        .from('calendar_sync_log')
        .select('*', { count: 'exact', head: true });

      if (count !== null) {
        setTotalSynced(count);
      }
    } catch (e) {
      console.error('Failed to fetch last sync from database:', e);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await CalendarSyncService.syncReleases();
      
      if (result.success) {
        const now = new Date().toISOString();
        setLastSyncTime(now);
        setLastSyncResult({
          created: result.created,
          updated: result.updated,
          skipped: result.skipped,
          errors: result.errors
        });
        
        localStorage.setItem('lastCalendarSync', now);
        localStorage.setItem('lastCalendarSyncResult', JSON.stringify({
          created: result.created,
          updated: result.updated,
          skipped: result.skipped,
          errors: result.errors
        }));
        
        toast.success(
          `Sync erfolgreich: ${result.created} erstellt, ${result.updated} aktualisiert, ${result.skipped} übersprungen`,
          { duration: 5000 }
        );
      } else {
        toast.error(`Sync fehlgeschlagen: ${result.errors.join(', ')}`);
        setLastSyncResult({
          created: 0,
          updated: 0,
          skipped: 0,
          errors: result.errors
        });
      }
    } catch (error) {
      console.error('Calendar sync error:', error);
      toast.error('Fehler beim Synchronisieren mit Google Calendar');
    } finally {
      setSyncing(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const success = await CalendarSyncService.testConnection();
      if (success) {
        toast.success('✅ Verbindung zu Google Calendar erfolgreich!');
      } else {
        toast.error('❌ Verbindung zu Google Calendar fehlgeschlagen');
      }
    } catch (error) {
      console.error('Connection test error:', error);
      toast.error('Fehler beim Testen der Verbindung');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Google Calendar Sync</h2>
        <p className="text-gray-400">
          Synchronisiere Releases automatisch mit dem Google Calendar (info@swipeup-marketing.com)
        </p>
      </div>

      <div className="bg-gray-800 rounded-lg p-6 space-y-6">
        {/* Sync Status */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Sync Status
          </h3>
          
          {lastSyncTime ? (
            <div className="bg-gray-700 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-300">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">
                    Letzter Sync: {new Date(lastSyncTime).toLocaleString('de-DE')}
                  </span>
                </div>
                {totalSynced > 0 && (
                  <div className="text-sm text-gray-400">
                    {totalSynced} Releases synchronisiert
                  </div>
                )}
              </div>
              
              {lastSyncResult && (
                <div className="grid grid-cols-3 gap-4 mt-3">
                  <div className="bg-green-900/30 border border-green-700 rounded p-3">
                    <div className="flex items-center gap-2 text-green-400 mb-1">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-xs font-medium">Erstellt</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{lastSyncResult.created}</p>
                  </div>
                  
                  <div className="bg-blue-900/30 border border-blue-700 rounded p-3">
                    <div className="flex items-center gap-2 text-blue-400 mb-1">
                      <RefreshCw className="w-4 h-4" />
                      <span className="text-xs font-medium">Aktualisiert</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{lastSyncResult.updated}</p>
                  </div>
                  
                  <div className="bg-gray-700 border border-gray-600 rounded p-3">
                    <div className="flex items-center gap-2 text-gray-400 mb-1">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-xs font-medium">Übersprungen</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{lastSyncResult.skipped}</p>
                  </div>
                </div>
              )}
              
              {lastSyncResult && lastSyncResult.errors.length > 0 && (
                <div className="bg-red-900/30 border border-red-700 rounded p-3 mt-3">
                  <div className="flex items-center gap-2 text-red-400 mb-2">
                    <XCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Fehler ({lastSyncResult.errors.length})</span>
                  </div>
                  <ul className="space-y-1 text-xs text-red-300">
                    {lastSyncResult.errors.map((error, idx) => (
                      <li key={idx}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gray-700 rounded-lg p-4 text-center text-gray-400">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Noch kein Sync durchgeführt</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-white">Aktionen</h3>
          
          <div className="flex gap-3">
            <button
              onClick={handleSync}
              disabled={syncing}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
            >
              <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Synchronisiere...' : 'Jetzt synchronisieren'}
            </button>
            
            <button
              onClick={handleTestConnection}
              disabled={testing || syncing}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
            >
              <Calendar className={`w-5 h-5 ${testing ? 'animate-pulse' : ''}`} />
              {testing ? 'Teste...' : 'Verbindung testen'}
            </button>
          </div>
        </div>

        {/* Info */}
        <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-400 mb-2">ℹ️ Wie funktioniert der Sync?</h4>
          <ul className="space-y-1 text-sm text-gray-300">
            <li>• Alle Releases mit Veröffentlichungsdatum werden synchronisiert</li>
            <li>• Format: <code className="bg-gray-700 px-1 rounded">[VÖ] Artist Name - Release Name</code></li>
            <li>• Events werden als ganztägige Ereignisse erstellt</li>
            <li>• Bestehende Events werden bei Datumsänderungen automatisch aktualisiert</li>
            <li>• Kalender: <code className="bg-gray-700 px-1 rounded">info@swipeup-marketing.com</code></li>
          </ul>
        </div>
      </div>
    </div>
  );
};
